import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { createApiUrlWithPrefix } from '../config/api';
import { Save, Plus, X, Settings as SettingsIcon, DollarSign, FileText, Shield, Clock } from 'lucide-react';
import toast from 'react-hot-toast';

const groupIcons = {
  'Rider Payments': DollarSign,
  'Owner Payments': DollarSign,
  'Jeton': SettingsIcon,
  'General': FileText,
  'Insurance': Shield,
  'Session': Clock,
};

const groupColors = {
  'Rider Payments': 'bg-blue-50 border-blue-200',
  'Owner Payments': 'bg-green-50 border-green-200',
  'Jeton': 'bg-purple-50 border-purple-200',
  'General': 'bg-gray-50 border-gray-200',
  'Insurance': 'bg-yellow-50 border-yellow-200',
  'Session': 'bg-indigo-50 border-indigo-200',
};

function AdminSettings() {
  const { user } = useAuth();
  const { refresh: refreshSettings } = useSettings();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSetting, setNewSetting] = useState({ key: '', value: '', description: '', group: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(createApiUrlWithPrefix('admin/settings'));
      setSettings(res.data);
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (setting) => {
    setEditingId(setting.id);
    setEditValue(setting.value || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveSetting = async (id) => {
    setSavingId(id);
    try {
      await axios.put(createApiUrlWithPrefix(`admin/settings/${id}`), { value: editValue });
      toast.success('Setting updated');
      setSettings(prev => prev.map(s => s.id === id ? { ...s, value: editValue } : s));
      setEditingId(null);
      refreshSettings();
    } catch (err) {
      toast.error('Failed to update setting');
    } finally {
      setSavingId(null);
    }
  };

  const addSetting = async () => {
    if (!newSetting.key.trim()) {
      toast.error('Key is required');
      return;
    }
    try {
      const res = await axios.post(createApiUrlWithPrefix('admin/settings'), newSetting);
      setSettings(prev => [...prev, res.data]);
      setShowAddForm(false);
      setNewSetting({ key: '', value: '', description: '', group: '' });
      toast.success('Setting added');
      refreshSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add setting');
    }
  };

  const deleteSetting = async (id) => {
    if (!window.confirm('Delete this setting?')) return;
    try {
      await axios.delete(createApiUrlWithPrefix(`admin/settings/${id}`));
      setSettings(prev => prev.filter(s => s.id !== id));
      toast.success('Setting deleted');
      refreshSettings();
    } catch (err) {
      toast.error('Failed to delete setting');
    }
  };

  const grouped = settings.reduce((acc, s) => {
    const g = s.group || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(s);
    return acc;
  }, {});

  const groupOrder = ['Rider Payments', 'Owner Payments', 'Jeton', 'General', 'Insurance', 'Session', 'Other'];

  if (user?.role !== 4 && user?.role !== 'SuperAdmin') {
    return <div className="p-8 text-center text-red-600 font-semibold">Access denied. SuperAdmin only.</div>;
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paramètres des Tarifs</h1>
          <p className="text-sm text-gray-500 mt-1">Gérez tous les prix et frais configurables du club</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          {showAddForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showAddForm ? 'Annuler' : 'Ajouter un paramètre'}
        </button>
      </div>

      {showAddForm && (
        <div className="mb-8 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Nouveau paramètre</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Clé (ex: lesson.extra.price)"
              value={newSetting.key}
              onChange={e => setNewSetting({ ...newSetting, key: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Valeur"
              value={newSetting.value}
              onChange={e => setNewSetting({ ...newSetting, value: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Description"
              value={newSetting.description}
              onChange={e => setNewSetting({ ...newSetting, description: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Groupe (ex: Rider Payments)"
              value={newSetting.group}
              onChange={e => setNewSetting({ ...newSetting, group: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <button
            onClick={addSetting}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
          >
            Ajouter
          </button>
        </div>
      )}

      <div className="space-y-6">
        {groupOrder.filter(g => grouped[g]).map(group => {
          const Icon = groupIcons[group] || SettingsIcon;
          const colorClass = groupColors[group] || 'bg-gray-50 border-gray-200';
          return (
            <div key={group} className={`rounded-xl border ${colorClass} overflow-hidden`}>
              <div className="flex items-center gap-3 px-5 py-3 border-b border-inherit bg-white/60">
                <Icon className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-800">{group}</h2>
                <span className="text-xs text-gray-400 ml-auto">{grouped[group].length} paramètre(s)</span>
              </div>
              <div className="divide-y divide-inherit">
                {grouped[group].map(setting => (
                  <div key={setting.id} className="px-5 py-3 flex items-center gap-4 hover:bg-white/40 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono text-gray-400 truncate">{setting.key}</div>
                      {setting.description && (
                        <div className="text-xs text-gray-500 mt-0.5">{setting.description}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {editingId === setting.id ? (
                        <>
                          <input
                            type="text"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            className="w-32 px-3 py-1.5 border-2 border-primary-400 rounded-lg text-sm font-semibold text-right bg-white"
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveSetting(setting.id);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                          />
                          <button
                            onClick={() => saveSetting(setting.id)}
                            disabled={savingId === setting.id}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          >
                            <Save className="h-4 w-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-lg font-bold text-gray-900 min-w-[80px] text-right">
                            {setting.value || <span className="text-gray-300 font-normal text-sm">—</span>}
                          </span>
                          <button
                            onClick={() => startEdit(setting)}
                            className="px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-primary-200"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => deleteSetting(setting.id)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {settings.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <SettingsIcon className="h-12 w-12 mx-auto mb-4 opacity-40" />
          <p>Aucun paramètre configuré. Ajoutez-en un avec le bouton ci-dessus.</p>
        </div>
      )}
    </div>
  );
}

export default AdminSettings;
